import { Framework } from '@/framework/instance';
import { Stage } from '@/framework/plugins';
import { Layer } from '@/layers/layer';
import { type TableLayer } from '@/layers/table-layer';
import { type TextLayer } from '@/layers/text-layer';
import { LayerType } from '@/version-types';
import { type TableAction } from '../table-edit';
import { type ClipboardPlugin } from './controller';
import { IClipboardData } from './types';

export class ClipboardPluginDependency extends Framework {
    addStages(stages, extraInfos, activeNewStage, scaleWidth, insertIndex) {
        const { disablePasteStage } = this.framework.getInstance<ClipboardPlugin>('ClipboardPlugin').config;

        if (disablePasteStage) {
            return;
        }

        this.framework
            .getInstance(Stage)
            .batchInsert(stages, extraInfos, activeNewStage, scaleWidth, undefined, insertIndex);
    }

    deleteStages(indexes) {
        return !!this.framework.getInstance(Stage).batchDelete(indexes);
    }

    addLayer(layer, options) {
        const { allowPasteLayerTypes } = this.framework.getInstance<ClipboardPlugin>('ClipboardPlugin').config;

        if (allowPasteLayerTypes.length > 0 && !allowPasteLayerTypes.includes(layer.layerType)) {
            return;
        }

        this.framework.getInstance(Layer).add(
            options
                ? {
                      layer,
                      ...options,
                  }
                : layer,
        );
    }

    deleteLayer(id) {
        this.framework.getInstance(Layer).delete(id);
    }

    private tryGetTableAction() {
        const tableAction = this.framework.getInstance<TableAction>('TableAction');

        if (!tableAction) {
            throw new Error('TableAction is not found');
        }

        return tableAction;
    }

    private tryGetTableLayer() {
        const tableLayer = this.framework.getInstance<TableLayer>('TableLayer');

        if (!tableLayer) {
            throw new Error('TableLayer is not found');
        }

        return tableLayer;
    }

    addTable(clipboardData: IClipboardData) {
        this.tryGetTableAction().emit('paste');
        return this.tryGetTableLayer().addTableLayerByClipboard({
            clipboardText: clipboardData.text,
            clipboardHTML: clipboardData.html,
        });
    }

    private tryGetTextLayer() {
        const textLayer = this.framework.getInstance<TextLayer>('TextLayer');

        if (!textLayer) {
            throw new Error('TextLayer is not found');
        }

        return textLayer;
    }

    addText(clipboardData: IClipboardData) {
        const { allowPasteLayerTypes } = this.framework.getInstance<ClipboardPlugin>('ClipboardPlugin').config;

        if (allowPasteLayerTypes.length > 0 && !allowPasteLayerTypes.includes(LayerType.TEXT_LAYER)) {
            return;
        }

        this.tryGetTextLayer().addByCustomLayerData({ text: clipboardData.text });
    }
}
